import React, { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Badge,
  Divider,
  TablePagination
} from '@mui/material';
import {
  Notifications as NotificationsIcon
} from '@mui/icons-material';
import { useAuthReady } from '../../hooks/useAuthReady';
import NotificationAccordion from '../../components/Alert/NotificationAccordion';
import { useNotificationsStore } from '../../store/notificationsStore';
import BackButton from '../../components/Button/BackButton';
import RefreshButton from '../../components/RefreshButton/RefreshButton';

const Alerts: React.FC = () => {
  const [searchParams] = useSearchParams();
  const notificationId = searchParams.get('notification');
  const isAuthReady = useAuthReady();
  const { 
    notifications, 
    getUnreadCount, 
    fetchNotifications,
    page,
    limit,
    total
  } = useNotificationsStore();
  const unreadCount = getUnreadCount();
  const hasSearchedForNotification = useRef<string | null>(null);
  const initialLoadDone = useRef(false);

  useEffect(() => {
    if (isAuthReady) {
      fetchNotifications({ silent: true });
      initialLoadDone.current = false;
      hasSearchedForNotification.current = null;
    }
  }, [isAuthReady, fetchNotifications]);

  useEffect(() => {
    if (notifications.length > 0 && total > 0 && !initialLoadDone.current) {
      initialLoadDone.current = true;
    }
  }, [notifications.length, total]);

  useEffect(() => {
    if (!notificationId || !isAuthReady) {
      return;
    }

    if (hasSearchedForNotification.current === notificationId) {
      return;
    }

    if (!initialLoadDone.current || total === 0 || notifications.length === 0) {
      return;
    }

    const searchNotification = async () => {
      const foundInCurrentPage = notifications.find(n => n.id === notificationId);
      if (foundInCurrentPage) {
        hasSearchedForNotification.current = notificationId;
        return;
      }

      hasSearchedForNotification.current = notificationId;
      const totalPages = Math.ceil(total / limit);
      
      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        if (pageNum === page) {
          continue;
        }

        try {
          await fetchNotifications({ page: pageNum, limit, silent: true });
          
          await new Promise(resolve => setTimeout(resolve, 200));
          
          const currentState = useNotificationsStore.getState();
          const found = currentState.notifications.find(n => n.id === notificationId);
          
          if (found) {
            return;
          }
        } catch (error) {
          console.error(`Error al buscar notificación en página ${pageNum}:`, error);
        }
      }
    };

    searchNotification();
  }, [notificationId, isAuthReady, notifications, total, page, limit, fetchNotifications]);

  const handleRefresh = () => {
    if (isAuthReady) {
      fetchNotifications({ page, limit });
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    if (isAuthReady) {
      fetchNotifications({ page: newPage + 1, limit });
    }
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLimit = parseInt(event.target.value, 10);
    if (isAuthReady) {
      fetchNotifications({ page: 1, limit: newLimit });
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <BackButton to="/" label="Volver al listado" />
      </Box>
      <Paper elevation={2} sx={{ p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: { xs: 'flex-start', md: 'space-between' },
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Badge badgeContent={unreadCount} color="primary">
                <NotificationsIcon fontSize="large" color="primary" />
              </Badge>
              <Box>
                <Typography 
                  variant="h4" 
                  component="h1" 
                  fontWeight="bold"
                  sx={{
                    fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
                  }}
                >
                  Alertas y Notificaciones
                </Typography>
                <Typography 
                  variant="subtitle1" 
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }}
                >
                  {unreadCount > 0 
                    ? `Tienes ${unreadCount} notificación${unreadCount > 1 ? 'es' : ''} sin leer`
                    : 'Todas las notificaciones están leídas'
                  }
                </Typography>
              </Box>
              <Box sx={{ marginBottom: 2.5 }}>
                <RefreshButton 
                  onRefresh={handleRefresh}
                  tooltip="Actualizar notificaciones"
                />
              </Box>
            </Box>       
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {notifications.length > 0 ? (
          <>
            <NotificationAccordion 
              notifications={notifications} 
              expandedNotificationId={notificationId}
            />
            <TablePagination
              rowsPerPageOptions={[5, 10, 20, 50]}
              component="div"
              count={total}
              rowsPerPage={limit}
              page={page - 1}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Notificaciones por página"
              labelDisplayedRows={({ from, to, count }) => 
                `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
              }
            />
          </>
        ) : (
          <Box 
            sx={{ 
              textAlign: 'center', 
              py: 8,
              color: 'text.secondary'
            }}
          >
            <NotificationsIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
            <Typography variant="h6" gutterBottom>
              No tienes notificaciones sin leer
            </Typography>
            <Typography variant="body2">
              Cuando recibas nuevas alertas aparecerán aquí
            </Typography>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default Alerts;